import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { byName } from '../memberSearch';
import { api, daysUntil, dueText, fmtDate, kr, type Event, type Registration } from '../api';
import { useData } from '../App';
import { DeleteButton, Empty, Icon, IconButton, MemberPicker } from '../ui';

const FORMATS = ['Commander', 'Sealed', 'Draft', 'Borðspil', 'Byrjendakvöld', 'Annað'];

const todayIso = () => new Date().toISOString().slice(0, 10);
type Filter = 'adofinni' | 'haldnir' | 'allt';

/**
 * Accepting one sign-up.
 *
 * The hard part is not the button, it is deciding who this is. A name is a
 * weak key — "Einar Hlynsson" and "Einar Ægir Hlynsson" are one person, and a
 * second member row would split his attendance across two people and quietly
 * skew the figures on the front page. So where the server found anyone it
 * might be, the choice is made here, deliberately, before anything is written.
 */
/** Words in a name, for comparing one against another in the browser. */
const words = (n: string) =>
  n.normalize('NFC').toLocaleLowerCase('is').split(/\s+/).filter(Boolean);

/**
 * Does the new name simply know more than the old one? "Soroush Jafari"
 * extends "Soroush"; "Jón Ólafsson" does not extend "Jón Jónsson". Only the
 * first case should offer to overwrite a name without being asked twice.
 */
function extendsName(existing: string, incoming: string): boolean {
  const a = words(existing);
  const b = words(incoming);
  return b.length > a.length && a.length > 0 && a.every((w) => b.includes(w));
}

function Accept({
  reg,
  members,
  busy,
  act,
}: {
  reg: Registration;
  members: { id: number; name: string }[];
  busy: boolean;
  act: (fn: () => Promise<Registration[]>) => Promise<void>;
}) {
  // Default to the best candidate when it is a near-certainty, otherwise to
  // making a new player — guessing a merge is worse than an extra row.
  const best = reg.suggestions[0];
  const [pick, setPick] = useState<string>(best && best.score >= 0.95 ? String(best.id) : '');
  const [rename, setRename] = useState<boolean | null>(null);

  const picked = pick ? members.find((m) => m.id === Number(pick)) : undefined;
  // Pre-ticked only when the sign-up plainly knows more of the name than the
  // roll does; any other difference is a decision, not a correction.
  const renameDefault = picked ? extendsName(picked.name, reg.name) : false;
  const doRename = rename ?? renameDefault;

  const suggested = new Set(reg.suggestions.map((s) => s.id));
  const rest = members
    .filter((m) => !suggested.has(m.id))
    .sort((a, b) => a.name.localeCompare(b.name, 'is'));

  return (
    <span className="reg-pick">
      <select
        value={pick}
        disabled={busy}
        aria-label={`Hver er ${reg.name}?`}
        onChange={(e) => {
          setPick(e.target.value);
          setRename(null);
        }}
      >
        <option value="">Nýr spilari — {reg.name}</option>
        {reg.suggestions.length > 0 && (
          <optgroup label="Líklega">
            {reg.suggestions.map((sg) => (
              <option key={sg.id} value={sg.id}>
                {sg.name} — {sg.why}
              </option>
            ))}
          </optgroup>
        )}
        {/* Everyone else, because the person reading this recognises people
            no rule can. A club roll is tens of names, not thousands. */}
        {rest.length > 0 && (
          <optgroup label="Allir spilarar">
            {rest.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </optgroup>
        )}
      </select>

      {picked && picked.name !== reg.name && (
        <label className="reg-rename">
          <input
            type="checkbox"
            checked={doRename}
            disabled={busy}
            onChange={(e) => setRename(e.target.checked)}
          />
          Breyta nafninu í „{reg.name}“
        </label>
      )}

      <span className="row" style={{ gap: 6 }}>
        <button type="button" className="btn sm" disabled={busy}
          onClick={() =>
            void act(() =>
              api.approveRegistration(reg.id, pick ? Number(pick) : undefined, doRename),
            )
          }>
          Samþykkja
        </button>
        <button type="button" className="btn ghost sm" disabled={busy}
          onClick={() => void act(() => api.declineRegistration(reg.id))}>
          Hafna
        </button>
      </span>
    </span>
  );
}

/**
 * Sign-ups from the public form, for one event.
 *
 * They sit here until somebody accepts them. Accepting is the only path from
 * the open internet into the club's member list, and it goes through a
 * person: it matches the name to an existing member or makes a new one, then
 * marks them attending.
 */
function Registrations({
  eventId,
  members,
  onApproved,
}: {
  eventId: number;
  members: { id: number; name: string }[];
  onApproved: () => Promise<void>;
}) {
  const [rows, setRows] = useState<Registration[] | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.registrations(eventId).then(setRows).catch(() => setRows([]));
  }, [eventId]);
  useEffect(load, [load]);

  async function act(fn: () => Promise<Registration[]>) {
    setBusy(true);
    try {
      setRows(await fn());
      await onApproved();
    } finally {
      setBusy(false);
    }
  }

  if (!rows || rows.length === 0) return null;
  const waiting = rows.filter((r) => r.status === 'new');

  return (
    <div className="fld">
      <span className="lbl">
        Skráningar af vefnum · {waiting.length} bíða af {rows.length}
      </span>
      <div className="regs">
        {rows.map((r) => (
          <div key={r.id} className={`reg reg-${r.status}`}>
            <span className="reg-who">
              <b>{r.name}</b>
              <span>{r.contact}</span>
            </span>
            {r.status === 'new' ? (
              <Accept reg={r} members={members} busy={busy} act={act} />
            ) : (
              <span className="row" style={{ gap: 6 }}>
                <span className="chip">{r.status === 'approved' ? 'Samþykkt' : 'Hafnað'}</span>
                <IconButton tone="del" label={`Eyða skráningu ${r.name}`}
                  onClick={() => void act(() => api.delRegistration(r.id))}>
                  <Icon.trash />
                </IconButton>
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Vidburdir() {
  const { events, members, memberName, refresh } = useData();

  const [filter, setFilter] = useState<Filter>('haldnir');
  const [openId, setOpenId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Partial<Event> | null>(null);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [date, setDate] = useState(todayIso);
  const [format, setFormat] = useState(FORMATS[0]);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [winner, setWinner] = useState('');
  const [spend, setSpend] = useState('');
  const [spendNote, setSpendNote] = useState('');
  const [income, setIncome] = useState('');
  const [incomeNote, setIncomeNote] = useState('');
  const [setCode, setSetCode] = useState('');
  const [busy, setBusy] = useState(false);

  const today = todayIso();
  const upcoming = events.filter((e) => e.event_date > today);
  const held = events.filter((e) => e.event_date <= today);
  const awaiting = held.filter((e) => e.attendees.length === 0);
  const planning = date > today;

  const shown =
    filter === 'adofinni'
      ? [...upcoming].sort((a, b) => a.event_date.localeCompare(b.event_date))
      : filter === 'haldnir'
        ? held
        : events;

  async function submit(ev: FormEvent) {
    ev.preventDefault();
    setBusy(true);
    try {
      await api.addEvent({
        name: name.trim(),
        event_date: date,
        format,
        attendees: [...picked],
        winner_member_id: winner ? Number(winner) : null,
        spend_isk: Number(spend) || 0,
        spend_note: spendNote.trim(),
        income_isk: Number(income) || 0,
        income_note: incomeNote.trim(),
        set_code: setCode.trim().toLowerCase(),
      });
      setName('');
      setPicked(new Set());
      setWinner('');
      setSpend('');
      setSpendNote('');
      setIncome('');
      setIncomeNote('');
      setSetCode('');
      if (planning) setFilter('adofinni');
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  function beginEdit(e: Event) {
    setOpenId(e.id);
    setDraft({ ...e, attendees: [...e.attendees] });
  }
  function cancel() {
    setOpenId(null);
    setDraft(null);
  }
  async function save() {
    if (openId === null || !draft) return;
    setSaving(true);
    try {
      await api.updateEvent(openId, {
        name: String(draft.name ?? '').trim(),
        event_date: draft.event_date,
        format: draft.format,
        attendees: draft.attendees ?? [],
        winner_member_id: draft.winner_member_id ?? null,
        spend_isk: Number(draft.spend_isk) || 0,
        spend_note: draft.spend_note ?? '',
        income_isk: Number(draft.income_isk) || 0,
        income_note: draft.income_note ?? '',
        set_code: (draft.set_code ?? '').toLowerCase(),
        registration_open: draft.registration_open ?? 0,
        capacity: Number(draft.capacity) || 0,
      });
      cancel();
      await refresh();
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: number) {
    if (!window.confirm('Eyða þessum viðburði?')) return;
    await api.delEvent(id);
    if (openId === id) cancel();
    await refresh();
  }

  return (
    <div className="view">
      {awaiting.length > 0 && (
        <div className="pending-note">
          <strong>
            {awaiting.length === 1
              ? 'Einn viðburður bíður skráningar.'
              : `${awaiting.length} viðburðir bíða skráningar.`}
          </strong>{' '}
          Dagurinn er liðinn en engin mæting skráð — opnaðu hann og hakaðu við hverjir mættu.
        </div>
      )}

      <div className="g-two">
        <section className="card col-8">
          <div className="card-h">
            <h2>Viðburðir</h2>
            <span className="lbl">
              {held.length} haldnir · {upcoming.length} á döfinni
            </span>
          </div>

          <div className="filters">
            {(
              [
                ['haldnir', 'Haldnir', held.length],
                ['adofinni', 'Á döfinni', upcoming.length],
                ['allt', 'Allt', events.length],
              ] as const
            ).map(([key, label, n]) => (
              <button
                key={key}
                type="button"
                className={`filt${filter === key ? ' on' : ''}`}
                aria-pressed={filter === key}
                onClick={() => setFilter(key)}
              >
                {label}
                <span className="n">{n}</span>
              </button>
            ))}
          </div>

          {shown.length === 0 ? (
            <Empty
              title={filter === 'adofinni' ? 'Ekkert á döfinni' : 'Engir viðburðir skráðir'}
              body={
                filter === 'adofinni'
                  ? 'Settu næsta fimmtudagskvöld á dagskrá með framtíðardagsetningu — mætingin kemur eftir á.'
                  : 'Skráðu kvöldið hér til hægri.'
              }
            />
          ) : (
            <div className="todos">
              {shown.map((e) => {
                const isPlanned = e.event_date > today;
                const needsLog = !isPlanned && e.attendees.length === 0;
                const editing = openId === e.id;
                const net = e.income_isk - e.spend_isk;

                return (
                  <div key={e.id} className={`todo-wrap${editing ? ' open' : ''}`}>
                    <div className="todo">
                      <button
                        type="button"
                        className="tx tx-btn"
                        aria-expanded={editing}
                        onClick={() => (editing ? cancel() : beginEdit(e))}
                      >
                        <span className="tx-main">
                          {e.name}
                          {isPlanned && <span className="chip gold ev-tag">Á döfinni</span>}
                          {needsLog && <span className="chip ev-tag warn">Vantar mætingu</span>}
                        </span>
                        <span className="ev-meta">
                          {fmtDate(e.event_date)}
                          {isPlanned && daysUntil(e.event_date) !== null
                            ? ` · ${dueText(e.event_date)}`
                            : ''}
                          {e.format ? ` · ${e.format}` : ''}
                          {!isPlanned ? ` · ${e.attendees.length} mættu` : ''}
                          {e.winner_member_id ? ` · ${memberName(e.winner_member_id)} vann` : ''}
                        </span>
                      </button>

                      {(e.income_isk > 0 || e.spend_isk > 0) && (
                        <span
                          className="num ev-money"
                          style={{ color: net >= 0 ? 'var(--ok)' : 'var(--warn)' }}
                        >
                          {net >= 0 ? '+' : '−'}
                          {kr(Math.abs(net))}
                        </span>
                      )}

                      <span className={`chev${editing ? ' up' : ''}`} aria-hidden="true">
                        <Icon.chev />
                      </span>
                      <DeleteButton label={`Eyða ${e.name}`} onClick={() => void remove(e.id)} />
                    </div>

                    {editing && draft && (
                      <div className="todo-body">
                        <div className="form">
                          <div className="fr">
                            <div className="fld">
                              <label className="lbl">Heiti</label>
                              <input
                                type="text"
                                value={String(draft.name ?? '')}
                                onChange={(x) => setDraft({ ...draft, name: x.target.value })}
                              />
                            </div>
                            <div className="fld">
                              <label className="lbl">Dagsetning</label>
                              <input
                                type="date"
                                value={draft.event_date ?? ''}
                                onChange={(x) => setDraft({ ...draft, event_date: x.target.value })}
                              />
                            </div>
                          </div>

                          <div className="fr">
                            <div className="fld">
                              <label className="lbl">Snið</label>
                              <select
                                value={draft.format ?? ''}
                                onChange={(x) => setDraft({ ...draft, format: x.target.value })}
                              >
                                {FORMATS.map((f) => (
                                  <option key={f}>{f}</option>
                                ))}
                              </select>
                            </div>
                            <div className="fld">
                              <label className="lbl">Sett (kóði)</label>
                              <input
                                type="text"
                                maxLength={8}
                                placeholder="fdn"
                                value={draft.set_code ?? ''}
                                onChange={(x) => setDraft({ ...draft, set_code: x.target.value })}
                              />
                            </div>
                          </div>

                          <div className="fld">
                            <span className="lbl">
                              Mæting · {(draft.attendees ?? []).length} valdir
                            </span>
                            <MemberPicker
                              members={members}
                              selected={draft.attendees ?? []}
                              onChange={(ids) => setDraft({ ...draft, attendees: ids })}
                            />
                          </div>

                          {/* Opening sign-ups is an explicit act per event —
                              nothing is published just by being in the future. */}
                          <div className="fr">
                            <div className="fld">
                              <label className="lbl">Opin skráning</label>
                              <select
                                value={draft.registration_open ? '1' : '0'}
                                onChange={(x) =>
                                  setDraft({ ...draft, registration_open: Number(x.target.value) })
                                }
                              >
                                <option value="0">Lokað</option>
                                <option value="1">Opin — birtist á skráningarsíðunni</option>
                              </select>
                            </div>
                            <div className="fld">
                              <label className="lbl" htmlFor="ev-cap">Hámarksfjöldi</label>
                              <input
                                id="ev-cap"
                                type="number"
                                min={0}
                                max={500}
                                value={draft.capacity ?? 0}
                                onChange={(x) => setDraft({ ...draft, capacity: Number(x.target.value) })}
                              />
                              <span className="hint">0 = engin takmörk</span>
                            </div>
                          </div>

                          <Registrations eventId={openId} members={members} onApproved={refresh} />

                          <div className="fr">
                            <div className="fld">
                              <label className="lbl">Sigurvegari</label>
                              <select
                                value={draft.winner_member_id ?? ''}
                                onChange={(x) =>
                                  setDraft({
                                    ...draft,
                                    winner_member_id: x.target.value ? Number(x.target.value) : null,
                                  })
                                }
                              >
                                <option value="">—</option>
                                {members
                                  .filter((m) => (draft.attendees ?? []).includes(m.id))
                                  .sort(byName)
                                  .map((m) => (
                                    <option key={m.id} value={m.id}>
                                      {m.name}
                                    </option>
                                  ))}
                              </select>
                            </div>
                            <div className="fld">
                              <label className="lbl">Kostnaður (kr.)</label>
                              <input
                                type="number"
                                min={0}
                                step={1}
                                value={String(draft.spend_isk ?? 0)}
                                onChange={(x) =>
                                  setDraft({ ...draft, spend_isk: Number(x.target.value) || 0 })
                                }
                              />
                            </div>
                          </div>

                          <div className="fr">
                            <div className="fld">
                              <label className="lbl">Tekjur (kr.)</label>
                              <input
                                type="number"
                                min={0}
                                step={1}
                                value={String(draft.income_isk ?? 0)}
                                onChange={(x) =>
                                  setDraft({ ...draft, income_isk: Number(x.target.value) || 0 })
                                }
                              />
                            </div>
                            <div className="fld">
                              <label className="lbl">Keypt fyrir viðburðinn</label>
                              <input
                                type="text"
                                value={String(draft.spend_note ?? '')}
                                onChange={(x) => setDraft({ ...draft, spend_note: x.target.value })}
                              />
                            </div>
                          </div>

                          <div className="row" style={{ justifyContent: 'flex-end', gap: 10 }}>
                            <button type="button" className="btn ghost sm" onClick={cancel}>
                              Hætta við
                            </button>
                            <button
                              type="button"
                              className="btn sm"
                              onClick={() => void save()}
                              disabled={saving || !String(draft.name ?? '').trim()}
                            >
                              {saving ? 'Vista…' : 'Vista'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="card col-4">
          <div className="card-h">
            <h2>{planning ? 'Setja á dagskrá' : 'Nýr viðburður'}</h2>
          </div>

          <form className="form" onSubmit={submit}>
            <div className="fld">
              <label className="lbl" htmlFor="ev-name">Heiti</label>
              <input
                id="ev-name"
                type="text"
                placeholder="Commander-kvöld"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="fr">
              <div className="fld">
                <label className="lbl" htmlFor="ev-date">Dagsetning</label>
                <input
                  id="ev-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
              </div>
              <div className="fld">
                <label className="lbl" htmlFor="ev-fmt">Snið</label>
                <select id="ev-fmt" value={format} onChange={(e) => setFormat(e.target.value)}>
                  {FORMATS.map((f) => (
                    <option key={f}>{f}</option>
                  ))}
                </select>
              </div>
            </div>

            {planning ? (
              <p className="hint" style={{ lineHeight: 1.55 }}>
                Framtíðardagur — viðburðurinn fer á dagskrá. Mæting, sigurvegari og uppgjör bíða
                þar til kvöldið er búið; opnaðu hann þá og fylltu inn.
              </p>
            ) : (
              <>
                <div className="fld">
                  <span className="lbl">Mæting · {picked.size} valdir</span>
                  <MemberPicker
                    members={members}
                    selected={[...picked]}
                    onChange={(ids) => setPicked(new Set(ids))}
                  />
                </div>

                <div className="fr">
                  <div className="fld">
                    <label className="lbl" htmlFor="ev-win">Sigurvegari</label>
                    <select id="ev-win" value={winner} onChange={(e) => setWinner(e.target.value)}>
                      <option value="">—</option>
                      {members
                        .filter((m) => picked.size === 0 || picked.has(m.id))
                        .sort(byName)
                        .map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="fld">
                    <label className="lbl" htmlFor="ev-spend">Kostnaður (kr.)</label>
                    <input
                      id="ev-spend"
                      type="number"
                      min={0}
                      step={1}
                      placeholder="0"
                      value={spend}
                      onChange={(e) => setSpend(e.target.value)}
                    />
                  </div>
                </div>

                <div className="fr">
                  <div className="fld">
                    <label className="lbl" htmlFor="ev-income">Tekjur (kr.)</label>
                    <input
                      id="ev-income"
                      type="number"
                      min={0}
                      step={1}
                      placeholder="0"
                      value={income}
                      onChange={(e) => setIncome(e.target.value)}
                    />
                  </div>
                  <div className="fld">
                    <label className="lbl" htmlFor="ev-inote">Greitt fyrir</label>
                    <input
                      id="ev-inote"
                      type="text"
                      placeholder="Þátttökugjöld"
                      value={incomeNote}
                      onChange={(e) => setIncomeNote(e.target.value)}
                    />
                  </div>
                </div>

                <div className="fld">
                  <label className="lbl" htmlFor="ev-note">Keypt fyrir viðburðinn</label>
                  <input
                    id="ev-note"
                    type="text"
                    placeholder="Play Booster Box, vinningar…"
                    value={spendNote}
                    onChange={(e) => setSpendNote(e.target.value)}
                  />
                </div>
              </>
            )}

            <div className="fld">
              <label className="lbl" htmlFor="ev-set">Sett (kóði)</label>
              <input
                id="ev-set"
                type="text"
                maxLength={8}
                placeholder="fdn"
                value={setCode}
                onChange={(e) => setSetCode(e.target.value)}
              />
              <span className="hint">Stýrir listaverkinu á forsíðunni.</span>
            </div>

            <button className="btn" type="submit" disabled={busy || !name.trim()}>
              {busy ? 'Vista…' : planning ? 'Setja á dagskrá' : 'Skrá viðburð'}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
